// Einsatzbezogene Risikobewertung nach EGRED 2 (BBK, Version 2.0) und den BOS-Standardszenarien (STS-BOS, V 1.0, Juni 2025).
//
// Ablauf wie EGRED 2 Kap. 4.2 (Abbildung 3):
//   Schritt 2 – Einsatzbezogene Risikobewertung (Kap. 4.2.2): offene Kategorie (Abbildung 1/2) oder
//               spezielle Kategorie unter Einhaltung eines Standardszenarios DE.STS.BOS-01/-02/-03
//   Schritt 3 – Einsatzspezifische Abweichung (Kap. 4.2.3): Güterabwägung, wenn Schritt 2 nicht zielführend
// Zusätzlich eine orientierende SORA-2.0-Einstufung (Anlage III: iGRC, Minderungen M1–M3, ARC, SAIL).
// Reine Funktion ohne UI, damit die Regeln nachvollziehbar und testbar bleiben.

export type Groesse = 'bis1' | 'bis3' | 'ab3'
export type Sicht = 'VLOS' | 'EVLOS' | 'BVLOS'
export type Gebiet = 'einsatzstelle' | 'laendlich' | 'urban' | 'ansammlung'
export type Luftraum = 'G' | 'ctr_freigabe' | 'ctr_ohne' | 'edr_freigabe' | 'edr_ohne'
export type Dichte = 'unbekannt' | 'f10' | 'f100' | 'max30' | 'max300'
export type Klasse = 'C0' | 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6' | 'keine'

export interface Eingaben {
  // Fluggerät
  groesse: Groesse            // charakteristische Dimension (Rotorspitze zu Rotorspitze diagonal)
  mtomAb25: boolean           // Startmasse ≥ 25 kg
  unter250?: boolean          // Startmasse unter 250 g (Bestandsdrohnen ohne Klasse dürfen in A1)
  klasse: Klasse              // EU-Klassenkennzeichnung (nur für die offene Kategorie relevant)
  hangar: boolean             // fest stationierte/gedockte Drohne
  // Betrieb
  sicht: Sicht
  hoehe: number               // maximale Flughöhe über Grund in m
  nacht: boolean
  beleuchtung: boolean
  mehrereDrohnen: boolean     // gleichzeitiger Einsatz mehrerer Drohnen
  // Gebiet
  gebiet: Gebiet
  bebaut: boolean             // Einsatzstelle liegt in bebautem Gebiet (für Luftrisiko / offene Kategorie)
  autobahn: boolean           // Bundesautobahn im Fluggebiet (zählt als urban)
  abstand30: boolean          // ≥ 30 m horizontal zu unbeteiligten Personen (A2)
  abstand150: boolean         // ≥ 150 m zu Wohn-, Gewerbe-, Industrie- und Erholungsgebieten (A3)
  // Luftraum
  luftraum: Luftraum
  heliport: boolean           // Heliport / PIS / Flugplatz tangiert
  koordiniert: boolean        // Absprache dazu erfolgt
  // Minderungen
  dichte: Dichte              // Gefährdetendichte im Fluggebiet
  dichteDokumentiert: boolean // vom BOS-Drohnensteuerer dokumentiert
  dichteBestaetigt: boolean   // beruht auf offiziellen Daten und von zweiter Person bestätigt
  rettung3min: boolean        // Rettungsmittel mit geschulten Kräften in 3 min an potenzieller Absturzstelle
  fallschirm: boolean         // Fallschirm-/Aufprallminderung (M2)
  // Voraussetzungen STS-BOS (Ergänzende Hinweise)
  einweisung: boolean         // Einsatzkräfte eingewiesen (Mustersicherheitseinweisung)
  fluggebiet: boolean         // Fluggebiet/Grenzabstand bestimmt (LBA-Leitfaden oder dipul-Volumenplaner)
  betriebshandbuch: boolean
  ausbildung: boolean         // Ausbildungsmodul STS-BOS
  // Einsatzspezifische Aspekte (Kap. 4.2.3)
  wetterOk: boolean           // innerhalb der Herstellerangaben
  luftverkehr: boolean        // weitere Luftfahrzeuge (z. B. Rettungshubschrauber) im Einsatzraum
  gefahren: boolean           // CBRN, Explosion, Hitze, Rauch im Einsatzraum
  externe: boolean            // Schaulustige, Zeitdruck, unübersichtliche Einsatzstelle, Hindernisse
  abgewogen: boolean          // Einsatzauftrag, Dringlichkeit und Fähigkeit abgeglichen
}

export const STANDARD: Eingaben = {
  groesse: 'bis1', mtomAb25: false, unter250: false, klasse: 'keine', hangar: false,
  sicht: 'VLOS', hoehe: 100, nacht: false, beleuchtung: false, mehrereDrohnen: false,
  gebiet: 'einsatzstelle', bebaut: false, autobahn: false, abstand30: false, abstand150: false,
  luftraum: 'G', heliport: false, koordiniert: false,
  dichte: 'unbekannt', dichteDokumentiert: false, dichteBestaetigt: false, rettung3min: false, fallschirm: false,
  einweisung: false, fluggebiet: false, betriebshandbuch: false, ausbildung: false,
  wetterOk: true, luftverkehr: false, gefahren: false, externe: false, abgewogen: false,
}

export type Ampel = 'gruen' | 'gelb' | 'rot'

export interface Sora {
  igrc: number | null         // drohnenbezogene Bodenrisikoklasse
  fgrc: number | null         // endgültige Bodenrisikoklasse
  minderung: string[]         // angerechnete Minderungen M1–M3
  arc: 'a' | 'b' | 'c' | 'd'
  arcGrund: string
  sail: string | null         // I–VI oder null (zulassungspflichtig)
}

export interface Ergebnis {
  ampel: Ampel
  titel: string
  kategorie: 'offen' | 'speziell' | 'zulassungspflichtig'
  szenario: string | null     // z. B. "DE.STS.BOS-03"
  zusammenfassung: string
  erfuellt: string[]          // angekreuzte bzw. erfüllte Bedingungen
  fehlend: string[]           // vor dem Flug noch zu erfüllen (nur der tatsächlich fehlende Teil)
  auflagen: string[]          // im Betrieb einzuhalten (Regeln des Szenarios/der Kategorie)
  ausschluss: string[]        // Gründe, warum kein Standardszenario greift
  hinweise: string[]          // einsatzspezifische Aspekte (Kap. 4.2.3)
  grenzabstand: { hoehe: number; normal: number; langsam: number }
  sora: Sora
  grundlage: string
}

const GRUNDLAGE = 'EGRED 2 (Kap. 4.2, Anlage II/III) und BOS-Standardszenarien STS-BOS V 1.0 (Juni 2025)'

/** STS-BOS Tabellen 1/2: Grenzabstand (Contingency Volume + Ground Risk Buffer), Multikopter CD 1,5 m */
const GRENZABSTAND: Array<[number, number, number]> = [
  [30, 69.7, 23.9], [50, 76.9, 26.7], [70, 83.1, 28.9], [100, 91.2, 31.8], [120, 96.0, 33.5],
]

export function grenzabstand(hoehe: number) {
  const zeile = GRENZABSTAND.find(([h]) => hoehe <= h) ?? GRENZABSTAND[GRENZABSTAND.length - 1]
  return { hoehe: zeile[0], normal: zeile[1], langsam: zeile[2] }
}

/** Gefährdetendichte erfüllt "um Faktor 10 niedriger … oder ≤ grenze Personen/km²" */
const faktor10 = (d: Dichte, grenze: 30 | 300) => d === 'f10' || d === 'f100' || d === 'max30' || (grenze === 300 && d === 'max300')
/** … "um Faktor 100 niedriger … oder ≤ 300 Personen/km²" */
const faktor100 = (d: Dichte) => d === 'f100' || d === 'max30' || d === 'max300'

const SAIL: Record<number, [string, string, string, string]> = {
  2: ['I', 'II', 'IV', 'VI'], 3: ['II', 'II', 'IV', 'VI'], 4: ['III', 'III', 'IV', 'VI'],
  5: ['IV', 'IV', 'IV', 'VI'], 6: ['V', 'V', 'V', 'VI'], 7: ['VI', 'VI', 'VI', 'VI'],
}

/** Orientierende SORA-2.0-Einstufung nach EGRED 2, Anlage III (Abbildungen 9–12) */
export function sora(e: Eingaben): Sora {
  const bvlos = e.sicht !== 'VLOS'
  const spalte = e.groesse === 'bis1' ? 0 : e.groesse === 'bis3' ? 1 : 2
  const urban = e.gebiet === 'urban' || e.autobahn
  const kontrolliert = [1, 2, 3][spalte]
  let igrc: number | null
  if (e.gebiet === 'ansammlung') igrc = spalte === 0 ? (bvlos ? 8 : 7) : null
  else if (e.gebiet === 'einsatzstelle') igrc = kontrolliert
  else if (urban) igrc = (bvlos ? [5, 6, 8] : [4, 5, 6])[spalte]
  else igrc = (bvlos ? [3, 4, 5] : [2, 3, 4])[spalte]

  const minderung: string[] = []
  let fgrc: number | null = null
  if (igrc !== null) {
    let m = 0
    if (e.gebiet !== 'einsatzstelle') {
      if (e.dichte === 'f100' || e.dichte === 'max30') { m -= 2; minderung.push('M1 mittel (−2): Gefährdetendichte um Faktor 100 reduziert') }
      else if (e.dichte === 'f10' || e.dichte === 'max300') { m -= 1; minderung.push('M1 niedrig (−1): Gefährdetendichte um Faktor 10 reduziert') }
    }
    if (e.fallschirm) { m -= 1; minderung.push('M2 mittel (−1): Reduktion der Auswirkungen des Bodenaufschlags') }
    if (e.rettung3min) { m -= 1; minderung.push('M3 hoch (−1): Notfallplan mit Rettungsmittel innerhalb 3 Minuten') }
    else if (e.betriebshandbuch) minderung.push('M3 mittel (±0): Notfallplan im Betriebshandbuch')
    else { m += 1; minderung.push('M3 fehlt (+1): kein Notfallplan') }
    // Die Minderungen können das Bodenrisiko nicht unter den Wert für einen kontrollierten Bereich senken
    fgrc = Math.max(kontrolliert, igrc + m)
  }

  let arc: Sora['arc']; let arcGrund: string
  if (e.luftraum === 'edr_freigabe') { arc = 'a'; arcGrund = 'Atypischer (gesperrter) Luftraum mit Freigabe' }
  else if (e.luftraum === 'ctr_freigabe' || e.luftraum === 'ctr_ohne') { arc = 'd'; arcGrund = 'Kontrollzone (Flugplatzumgebung, Luftraum D)' }
  else if (e.luftraum === 'edr_ohne') { arc = 'd'; arcGrund = 'Flugbeschränkungsgebiet ohne Freigabe' }
  else if (e.heliport) { arc = 'c'; arcGrund = 'Unkontrollierter Luftraum in Heliport-/Flugplatzumgebung' }
  else if (e.hoehe > 150) { arc = 'c'; arcGrund = 'Über 500 ft (150 m) über Grund' }
  else if (urban || (e.gebiet === 'einsatzstelle' && e.bebaut)) { arc = 'c'; arcGrund = 'Unkontrollierter Luftraum über bebautem Gebiet' }
  else { arc = 'b'; arcGrund = 'Unkontrollierter Luftraum über ländlichem Gebiet' }

  const sail = fgrc === null || fgrc > 7 ? null : SAIL[Math.max(2, fgrc)][['a', 'b', 'c', 'd'].indexOf(arc)]
  return { igrc, fgrc, minderung, arc, arcGrund, sail }
}

/** Prüft die offene Kategorie (EGRED 2 Abbildung 1/2); Rückgabe der Unterkategorie oder null */
function offeneKategorie(e: Eingaben): { unter: 'A1' | 'A2' | 'A3'; regeln: string[] } | null {
  if (e.sicht !== 'VLOS' || e.hoehe > 120 || e.mtomAb25 || e.mehrereDrohnen || e.gebiet === 'ansammlung' || e.luftraum !== 'G') return null
  const basis = ['Flug in Sichtweite (VLOS)', 'Maximal 120 m über Grund', 'Keine Menschenansammlungen überfliegen']
  // A1 auch für Bestandsdrohnen ohne Klasse unter 250 g (Art. 20 DVO (EU) 2019/947)
  if (e.klasse === 'C0' || e.klasse === 'C1' || (e.klasse === 'keine' && e.unter250))
    return { unter: 'A1', regeln: [...basis, 'Überflug Unbeteiligter vermeiden, bei unerwartetem Überflug so kurz wie möglich', 'EU-Kompetenznachweis A1/A3'] }
  if (e.klasse === 'C2' && e.abstand30)
    return { unter: 'A2', regeln: [...basis, '30 m horizontaler Abstand zu Unbeteiligten (5 m im Langsamflugmodus)', 'Fernpilotenzeugnis A2'] }
  // A3: C2–C4 oder Bestandsdrohne ohne Klasse; C5/C6 sind für Standardszenarien und nicht für die offene Kategorie
  if (['C2', 'C3', 'C4', 'keine'].includes(e.klasse) && e.abstand150 && e.gebiet !== 'urban' && !e.autobahn && !(e.gebiet === 'einsatzstelle' && e.bebaut))
    return { unter: 'A3', regeln: [...basis, 'Keine Gefährdung Unbeteiligter', '150 m Abstand zu Wohn-, Gewerbe-, Industrie- und Erholungsgebieten', 'EU-Kompetenznachweis A1/A3'] }
  return null
}

/** Hinweise aus Kap. 4.2.3 und den Ergänzenden Hinweisen der STS-BOS */
function einsatzHinweise(e: Eingaben): string[] {
  const h: string[] = []
  if (e.luftverkehr) h.push('Weitere Luftfahrzeuge im Einsatzraum: Luftraumbeobachter einsetzen, mit Abschnittsleitung/Flugleitung abstimmen, bemanntem Luftverkehr immer ausweichen.')
  if (e.gefahren) h.push('Besondere Gefahrenquellen (CBRN, Explosion, Hitze, Rauch): Schutzvorkehrungen treffen, zusätzliche Sicherheitsabstände/Löschmittel; bei möglicher Kontamination im kontaminierten Bereich landen.')
  if (e.externe) h.push('Externe Einflüsse (Schaulustige, Zeitdruck, unübersichtliche Einsatzstelle, Hindernisse): Absperrung prüfen, Flugroute so wählen, dass niemand überflogen wird.')
  if (!e.abgewogen) h.push('Einsatzauftrag, Dringlichkeit und Fähigkeit abgleichen: Das Risiko ist immer mit dem Zweck des Auftrags abzuwägen, ggf. ein anderes Einsatzmittel wählen.')
  if (e.mtomAb25) h.push('Startmasse ≥ 25 kg: Abschirmung von Personen in Gebäuden ist nicht anrechenbar; individuelle Betrachtung empfohlen.')
  if (e.sicht === 'EVLOS') h.push('EVLoS wird für die Bodenrisiko-Bedingungen der Standardszenarien vorsichtshalber wie BVLoS bewertet.')
  return h
}

export function bewerte(e: Eingaben): Ergebnis {
  const s = sora(e)
  const ga = grenzabstand(Math.min(e.hoehe, 120))
  const hinweise = einsatzHinweise(e)
  const grund = { grenzabstand: ga, sora: s, grundlage: GRUNDLAGE, hinweise }

  // Harte Ausschlüsse, unabhängig von der Kategorie
  const sperre: string[] = []
  if (!e.wetterOk) sperre.push('Wetter außerhalb der Herstellerangaben – bei Zweifeln ist nicht zu fliegen (Anlage II).')
  if (e.luftraum === 'ctr_ohne') sperre.push('Kontrollzone ohne Flugverkehrskontrollfreigabe bzw. ohne passende NfL – BOS sind nicht befreit.')
  if (e.luftraum === 'edr_ohne') sperre.push('Flugbeschränkungsgebiet (ED-R) ohne Freigabe des Verantwortlichen – der Betrieb wird geahndet, BOS sind nicht befreit.')
  if (sperre.length) {
    return {
      ...grund, ampel: 'rot', titel: 'Kein Flug', kategorie: 'speziell', szenario: null,
      zusammenfassung: 'Unter diesen Bedingungen darf nicht geflogen werden. Erst die genannten Punkte klären.',
      erfuellt: [], auflagen: [], fehlend: [], ausschluss: sperre,
    }
  }

  // Schritt 2a: offene Kategorie
  const offen = offeneKategorie(e)
  if (offen) {
    return {
      ...grund, ampel: 'gruen', titel: `Offene Kategorie ${offen.unter}`, kategorie: 'offen', szenario: null,
      zusammenfassung: `Der Flug ist in der offenen Kategorie, Unterkategorie ${offen.unter}, möglich. Eine Risikobewertung nach SORA ist nicht erforderlich.`,
      erfuellt: [], auflagen: [...offen.regeln, 'Checklisten nach EGRED 2 Anlage IV abarbeiten'], fehlend: [], ausschluss: [],
    }
  }

  // Schritt 2b: spezielle Kategorie mit BOS-Standardszenario
  const ausschluss: string[] = []
  if (e.gebiet === 'ansammlung') ausschluss.push('Überflug von Menschenansammlungen ist in keinem Standardszenario vorgesehen und sollte vermieden werden (Kap. 4.2.2); je nach Drohne Kategorie „zulassungspflichtig“.')
  if (e.hoehe > 120) ausschluss.push('Flughöhe über 120 m über Grund – die Standardszenarien erlauben maximal 120 m.')
  if (e.groesse === 'ab3') ausschluss.push('Charakteristische Dimension ab 3 m – die Standardszenarien gelten nur für Drohnen unter 3 m.')
  if (e.hangar) ausschluss.push('Fest stationierte/gedockte Drohne (Hangar) – individuelle Risikobetrachtung und Betriebshandbuch nötig.')

  if (ausschluss.length) {
    const zulassungspflichtig = s.fgrc === null || s.fgrc > 7
    return {
      ...grund, ampel: 'rot', titel: zulassungspflichtig ? 'Kein Standardszenario – Kategorie „zulassungspflichtig“' : 'Kein Standardszenario anwendbar',
      kategorie: zulassungspflichtig ? 'zulassungspflichtig' : 'speziell', szenario: null,
      zusammenfassung: 'Kein BOS-Standardszenario passt. Möglich ist nur eine individuelle Risikobewertung (SORA) oder eine einsatzspezifische Abweichung nach Güterabwägung (Kap. 4.2.3), dokumentiert und von der Einsatzleitung getragen.',
      auflagen: ['Einsatzspezifische Abweichung nur, wenn für die Aufgabenerfüllung zwingend nötig (Güterabwägung/Verhältnismäßigkeit)', 'Führungsvorgang nach DV 100 zugrunde legen', 'Abwägung und Entscheidung dokumentieren'],
      erfuellt: [], fehlend: [], ausschluss,
    }
  }

  const urban = e.gebiet === 'urban' || e.autobahn
  const bvlos = e.sicht !== 'VLOS'
  const gross = e.groesse === 'bis3'
  const szenario = e.gebiet === 'einsatzstelle' ? 'DE.STS.BOS-01' : urban ? 'DE.STS.BOS-03' : 'DE.STS.BOS-02'
  const name = { 'DE.STS.BOS-01': 'Flug über Einsatzstellen', 'DE.STS.BOS-02': 'Flug über ländlichem Gebiet', 'DE.STS.BOS-03': 'Flug im urbanen Gebiet' }[szenario]

  const auflagen = [
    'Betrieb im SAIL II, ausschließlich mit der aktuellen Fassung der EGRED 2',
    `Maximale Flughöhe 120 m über Grund (geplant: ${e.hoehe} m)`,
    'Kein Flug über Menschenansammlungen; Flugroute so wählen, dass möglichst niemand überflogen wird',
    `Grenzabstand (Contingency Volume + Ground Risk Buffer) bei ${ga.hoehe} m Flughöhe: ${ga.normal.toLocaleString('de-DE')} m, im Langsamflug ${ga.langsam.toLocaleString('de-DE')} m (Faustregel: 100 m Abstand)`,
  ]
  if (szenario === 'DE.STS.BOS-01') auflagen.push('Kontrollierter Bereich am Boden = Einsatzgebiet: Zutritt Unbeteiligter verhindern, äußere Grenze des Ground Risk Buffer = Absperrung')
  if (szenario === 'DE.STS.BOS-02' && bvlos) auflagen.push('BVLoS: Ground Risk Buffer darf sich nicht mit bebauten Gebieten (Wohn-, Gewerbe-, Industrie-, Erholungsgebiete) überschneiden')
  if (e.luftraum === 'ctr_freigabe') auflagen.push('Kontrollzone: Anweisungen des Towers bzw. Rahmenbedingungen der NfL einhalten')
  if (e.luftraum === 'edr_freigabe') auflagen.push('ED-R: Bedingungen der Freigabe und der NfL/NOTAM einhalten')

  const fehlend: string[] = []
  const erfuellt: string[] = []
  const dichteText = (grenze: string, belegt: boolean) =>
    `Gefährdetendichte ${grenze}${belegt ? ', auf offiziellen Daten beruhend und von einer zweiten Person bestätigt' : ', vom BOS-Drohnensteuerer dokumentiert'}`
  const pruefeDichte = (ok: boolean, grenze: string, belegt: boolean) => {
    const text = dichteText(grenze, belegt)
    if (ok && e.dichteDokumentiert && (!belegt || e.dichteBestaetigt)) erfuellt.push(text)
    else fehlend.push(text)
  }
  const pruefeRettung = () => {
    const text = 'Rettungsmittel mit geschulten Einsatzkräften innerhalb von 3 Minuten (ca. 800 m fußläufig) an der potenziellen Absturzstelle'
    if (e.rettung3min) erfuellt.push(text); else fehlend.push(text)
  }

  if (szenario === 'DE.STS.BOS-02' && bvlos && gross) pruefeDichte(faktor10(e.dichte, 30), 'um Faktor 10 niedriger als die Bevölkerungsdichte oder ≤ 30 Personen/km²', false)
  if (szenario === 'DE.STS.BOS-03') {
    if (!bvlos && !gross) pruefeRettung()
    if (!bvlos && gross) { pruefeDichte(faktor10(e.dichte, 300), 'um Faktor 10 niedriger als die Bevölkerungsdichte oder ≤ 300 Personen/km²', false); pruefeRettung() }
    if (bvlos && gross) { pruefeRettung(); pruefeDichte(faktor100(e.dichte), 'um Faktor 100 niedriger als die Bevölkerungsdichte oder ≤ 300 Personen/km²', true) }
    if (bvlos && !gross) {
      // Variante 1: Faktor 100 (belegt) – Variante 2: Rettungsmittel + Faktor 10 (belegt).
      // Angezeigt wird je Variante nur, was davon noch fehlt.
      const belegtFehlt = [
        ...(e.dichteDokumentiert ? [] : ['Gefährdetendichte dokumentieren']),
        ...(e.dichteBestaetigt ? [] : ['auf offizielle Daten stützen und von einer zweiten Person bestätigen lassen']),
      ]
      const v1Fehlt = [...(faktor100(e.dichte) ? [] : ['Gefährdetendichte um Faktor 100 niedriger als die Bevölkerungsdichte oder ≤ 300 Personen/km²']), ...belegtFehlt]
      const v2Fehlt = [
        ...(e.rettung3min ? [] : ['Rettungsmittel innerhalb von 3 Minuten an der potenziellen Absturzstelle']),
        ...(faktor10(e.dichte, 300) ? [] : ['Gefährdetendichte um Faktor 10 niedriger als die Bevölkerungsdichte oder ≤ 300 Personen/km²']),
        ...belegtFehlt,
      ]
      if (!v1Fehlt.length) erfuellt.push(dichteText('um Faktor 100 niedriger als die Bevölkerungsdichte oder ≤ 300 Personen/km² (Variante 1)', true))
      else if (!v2Fehlt.length) {
        erfuellt.push('Rettungsmittel mit geschulten Einsatzkräften innerhalb von 3 Minuten an der potenziellen Absturzstelle (Variante 2)')
        erfuellt.push(dichteText('um Faktor 10 niedriger als die Bevölkerungsdichte oder ≤ 300 Personen/km² (Variante 2)', true))
      } else {
        if (e.rettung3min) erfuellt.push('Rettungsmittel mit geschulten Einsatzkräften innerhalb von 3 Minuten (Teil von Variante 2)')
        fehlend.push('Variante 1 – es fehlt: ' + v1Fehlt.join('; '))
        fehlend.push('oder Variante 2 – es fehlt: ' + v2Fehlt.join('; '))
      }
    }
  }

  // Voraussetzungen aus den Ergänzenden Hinweisen der STS-BOS
  const voraus: Array<[boolean, string]> = [
    [e.einweisung, 'Einsatzkräfte über den Drohneneinsatz eingewiesen (Mustersicherheitseinweisung), sonst gelten sie als Unbeteiligte'],
    [e.fluggebiet, 'Fluggebiet und Grenzabstand vor Beginn bestimmt (LBA-Leitfaden oder dipul-Volumenplaner) und dokumentiert'],
    [e.betriebshandbuch, `Betriebshandbuch für ${szenario} vorhanden`],
    [e.ausbildung, 'BOS-Drohnensteuerer im Ausbildungsmodul STS-BOS geschult'],
  ]
  if (e.nacht) voraus.push([e.beleuchtung, 'Nachtflug: Beleuchtung der Drohne nach EGRED 2 vorhanden und eingeschaltet'])
  if (e.heliport) voraus.push([e.koordiniert, 'Heliport/PIS/Flugplatz tangiert: Absprache/Koordinierung durchgeführt'])
  for (const [ok, text] of voraus) (ok ? erfuellt : fehlend).push(text)

  const vollstaendig = fehlend.length === 0
  return {
    ...grund,
    ampel: vollstaendig ? 'gruen' : 'gelb',
    titel: `${szenario} – ${name}`,
    kategorie: 'speziell', szenario,
    zusammenfassung: vollstaendig
      ? `Der Flug ist in der speziellen Kategorie nach dem Standardszenario ${szenario} (${name}, ${bvlos ? 'BVLoS' : 'VLoS'}) möglich.`
      : `Das Standardszenario ${szenario} (${name}) kommt in Frage. Vor dem Flug müssen noch die offenen Punkte erfüllt werden.`,
    erfuellt, auflagen, fehlend, ausschluss: [],
  }
}
